import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

/** The canvas track is sent to the peer as well as shown in the local preview. */
export async function createBackgroundBlur(source) {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = source;
  const canvas = document.createElement('canvas');
  if (!canvas.captureStream) throw new Error('Background blur is unavailable in this browser.');
  const ctx = canvas.getContext('2d');
  const segmenter = new SelfieSegmentation({
    locateFile: file => `${process.env.PUBLIC_URL || ''}/call-effects/${file}`,
  });
  segmenter.setOptions({ modelSelection: 1 });
  let stopped = false;
  let timer;
  let stream;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    stream?.getTracks().forEach(track => track.stop());
    video.pause();
    video.srcObject = null;
    // Let in-flight inference finish before disposing its WASM context.
    void pending.finally(() => segmenter.close()).catch(() => {});
  };
  let pending = Promise.resolve();
  segmenter.onResults(({ image, segmentationMask }) => {
    if (stopped) return;
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(segmentationMask, 0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'destination-over';
    ctx.filter = 'blur(14px)';
    ctx.drawImage(image, -16, -16, canvas.width + 32, canvas.height + 32);
    ctx.restore();
  });
  try {
    await video.play();
    const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.max(2, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(2, Math.round(video.videoHeight * scale));
    pending = segmenter.send({ image: video });
    await pending;
    stream = canvas.captureStream(24);
    const tick = async () => {
      if (stopped) return;
      try {
        pending = segmenter.send({ image: video });
        await pending;
      } catch {
        // Never freeze an identifiable frame if the processor fails mid-call.
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      if (!stopped) timer = setTimeout(tick, 1000 / 24);
    };
    timer = setTimeout(tick, 1000 / 24);
    return { stream, stop };
  } catch (error) {
    stop();
    throw new Error(`Could not start background blur: ${error.message}`);
  }
}
